import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { PoliticianData } from '../types';

interface SEOHeadProps {
  politician?: PoliticianData | null;
  timeframe?: string;
}

// Format currency for display
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Calculate return percentage
const calculateReturn = (currentValue: number, initialValue: number = 10000): number => {
  return ((currentValue - initialValue) / initialValue) * 100;
};

// Generate URL-friendly slug from politician name
export const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

// Get politician URL
export const getPoliticianUrl = (politician: PoliticianData, baseUrl: string = 'https://capitol-alpha.com'): string => {
  return `${baseUrl}/politician/${generateSlug(politician.name)}`;
};

const SEOHead: React.FC<SEOHeadProps> = ({ politician, timeframe = '1Y' }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  // Map language codes to locale codes
  const localeMap: Record<string, string> = {
    'en': 'en_US',
    'zh-CN': 'zh_CN',
    'zh-TW': 'zh_TW',
  };
  const ogLocale = localeMap[currentLang] || 'en_US';

  const baseUrl = 'https://capitol-alpha.com';

  // Generate politician-specific meta tags
  if (politician) {
    const returnPct = calculateReturn(politician.currentValue);
    const returnSign = returnPct >= 0 ? '+' : '';
    const formattedReturn = `${returnSign}${returnPct.toFixed(1)}%`;
    const formattedValue = formatCurrency(politician.currentValue);

    // Dynamic title and description
    const title = `${politician.name}'s Stock Trades - ${formattedReturn} Return | Capitol Alpha`;
    const description = `Track ${politician.name}'s (${politician.party}, ${politician.state}) congressional stock trades. ${timeframe} simulated return: ${formattedReturn} (${formattedValue} from $10,000). View portfolio holdings, trade history, and performance.`;

    // Politician page URL
    const pageUrl = getPoliticianUrl(politician, baseUrl);

    // OG image - can be a template image or generate dynamically
    const ogImageUrl = `${baseUrl}/og-politician.png`;

    return (
      <Helmet>
        {/* Primary Meta Tags */}
        <html lang={currentLang} />
        <title>{title}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:site_name" content="Capitol Alpha" />

        {/* Profile specific tags */}
        <meta property="profile:first_name" content={politician.name.split(' ')[0]} />
        <meta property="profile:last_name" content={politician.name.split(' ').slice(1).join(' ')} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImageUrl} />

        {/* Additional meta tags for search */}
        <meta name="keywords" content={`${politician.name}, congressional trading, stock trades, ${politician.party}, ${politician.state}, ${politician.chamber}, politician investments`} />
      </Helmet>
    );
  }

  // Default meta tags for main page
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={currentLang} />
      <title>{t('meta:title')}</title>
      <meta name="title" content={t('meta:title')} />
      <meta name="description" content={t('meta:description')} />
      <meta name="keywords" content={t('meta:keywords')} />
      <link rel="canonical" href={baseUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={baseUrl} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:title" content={t('meta:ogTitle')} />
      <meta property="og:description" content={t('meta:ogDescription')} />
      <meta property="og:image" content={`${baseUrl}/og-image.png`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Capitol Alpha" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={baseUrl} />
      <meta name="twitter:title" content={t('meta:twitterTitle')} />
      <meta name="twitter:description" content={t('meta:twitterDescription')} />
      <meta name="twitter:image" content={`${baseUrl}/og-image.png`} />
    </Helmet>
  );
};

export default SEOHead;
